import { createReducer } from 'helpers/redux';
import { cleanAccessories } from 'src/helpers/accessories';
import { cleanPacks } from 'helpers/packs';
import {
  SET_STEP,
  GO_TO_PREV_STEP,
  GO_TO_NEXT_STEP,
  TOGGLE_SIDE,
  TOGGLE_FULLSCREEN,
  SET_ACCESSORIES_CATEGORY,
  SET_ACCESSORIES_SORT,
  SET_ACCESSORIES_QUERY,
  SET_ACCESSORIES_PAGE,
  OPEN_MODAL,
  CLOSE_MODAL,
  OPEN_DISCLAIMER,
  CLOSE_DISCLAIMER,
  SET_SELECTED_DISCLAIMER,
  RESET_CONFIGURATOR,
  SET_ACCESSORY_ITEMS,
  SET_ACCESSORY_CATEGORIES,
  SET_ACCESSORY_EXTRAS,
  PREVIEW_ACCESSORY,
  FLIP_BIKE,
  CLOSE_ALL_MODALS,
  ZOOM_ON,
  ZOOM_OFF,
  SET_SCROLL_Y_POSITION,
  SET_SCROLL_X_POSITION,
  SET_PACKS,
  SET_PRINT_ACTIONS,
  SET_MODAL_PROPS,
  SET_PROVINCES,
  SET_PROVINCE_PRICING_ENABLED,
  SET_SELECTED_PROVINCE,
} from './types';

export const initialState = {
  step: 2,
  completedSteps: [1],
  side: 'right',
  fullscreen: false,
  flipped: false,
  zoom: false,
  provinces: [],
  selectedProvince: null,
  provincePricingEnabled: false,
  packs: {
    items: [],
  },
  accessories: {
    categories: [],
    category: null,
    items: [],
    page: 1,
    previewedAccessory: null,
    query: '',
    sort: '',
  },
  openModals: {
    accessories: false,
    colour: false,
    confirm: false,
    disclaimer: false,
    expand: false,
    loadGarage: false,
    saveToGarage: false,
    saveToGarageAbandon: false,
    sendToDealer: false,
    share: false,
    tray: false,
    unsavedConfiguration: false,
    unsavedConfigurationAbandon: false,
  },
  modalProps: {
    confirm: {},
    reset: {},
    saveToGarage: {},
    sendToDealer: {},
  },
  disclaimer: {
    open: false,
    type: null,
  },
  scrollYPosition: 0,
  scrollXPosition: 0,
  printActions: 0,
};

const configuratorReducer = createReducer({
  initialState,
  reducers: {
    [SET_STEP]: (state, action) => {
      const currentStep = state.step;
      const completedSteps = state.completedSteps.includes(currentStep)
        ? state.completedSteps
        : state.completedSteps.concat([currentStep]);

      return {
        ...state,
        step: action.payload,
        completedSteps,
      };
    },
    [GO_TO_PREV_STEP]: state => ({
      ...state,
      step: state.step > 1 ? state.step - 1 : 1,
    }),
    [GO_TO_NEXT_STEP]: state => ({
      ...state,
      step: state.step + 1,
    }),
    [TOGGLE_SIDE]: state => ({
      ...state,
      side: state.side === 'right' ? 'left' : 'right',
    }),
    [TOGGLE_FULLSCREEN]: state => ({
      ...state,
      fullscreen: !state.fullscreen,
    }),
    [SET_ACCESSORIES_CATEGORY]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        category: action.payload,
      },
    }),
    [SET_ACCESSORIES_SORT]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        sort: action.payload,
      },
    }),
    [SET_ACCESSORIES_QUERY]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        query: action.payload,
      },
    }),
    [SET_ACCESSORIES_PAGE]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        page: action.payload,
      },
    }),
    [SET_MODAL_PROPS]: (state, action) => ({
      ...state,
      modalProps: {
        ...state.modalProps,
        [action.payload.modalName]: action.payload.props || {},
      },
    }),
    [OPEN_MODAL]: (state, action) => ({
      ...state,
      openModals: {
        ...state.openModals,
        [action.payload.modalName]: true,
      },
      modalProps: {
        ...state.modalProps,
        [action.payload.modalName]: action.payload.props || {},
      },
    }),
    [CLOSE_MODAL]: (state, action) => ({
      ...state,
      openModals: {
        ...state.openModals,
        [action.payload.name]: false,
      },
      modalProps: {
        ...(action.payload.clearProps ? initialState.modalProps : state.modalProps),
      },
      accessories: {
        ...state.accessories,
        previewedAccessory: null,
      },
    }),
    [OPEN_DISCLAIMER]: state => ({
      ...state,
      disclaimer: {
        ...state.disclaimer,
        open: true,
      },
    }),
    [CLOSE_DISCLAIMER]: state => ({
      ...state,
      disclaimer: {
        ...state.disclaimer,
        open: false,
      },
    }),
    [SET_SELECTED_DISCLAIMER]: (state, action) => ({
      ...state,
      disclaimer: {
        ...state.disclaimer,
        type: action.payload,
      },
    }),
    [RESET_CONFIGURATOR]: () => ({
      ...initialState,
    }),
    [SET_ACCESSORY_ITEMS]: (state, { payload: accessoryItems }) => ({
      ...state,
      accessories: {
        ...state.accessories,
        items: cleanAccessories(accessoryItems),
      },
    }),
    [SET_ACCESSORY_CATEGORIES]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        categories: action.payload,
      },
    }),
    [SET_ACCESSORY_EXTRAS]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        extras: action.payload,
      },
    }),
    [PREVIEW_ACCESSORY]: (state, action) => ({
      ...state,
      accessories: {
        ...state.accessories,
        previewedAccessory: action.payload,
      },
    }),
    [FLIP_BIKE]: state => ({
      ...state,
      flipped: !state.flipped,
    }),
    [CLOSE_ALL_MODALS]: state => ({
      ...state,
      openModals: {
        ...initialState.openModals,
      },
    }),
    [ZOOM_ON]: state => ({
      ...state,
      zoom: true,
    }),
    [ZOOM_OFF]: state => ({
      ...state,
      zoom: false,
    }),
    [SET_SCROLL_Y_POSITION]: (state, { payload: scrollYPosition }) => ({
      ...state,
      scrollYPosition,
    }),
    [SET_SCROLL_X_POSITION]: (state, { payload: scrollXPosition }) => ({
      ...state,
      scrollXPosition,
    }),
    [SET_PACKS]: (state, { payload: packs }) => ({
      ...state,
      packs: {
        ...state.packs,
        items: cleanPacks(packs, state.accessories.items),
      },
    }),
    [SET_PRINT_ACTIONS]: (state, { payload: printActions }) => ({
      ...state,
      printActions,
    }),
    [SET_PROVINCES]: (state, { payload: provinces }) => ({
      ...state,
      provinces,
    }),
    [SET_PROVINCE_PRICING_ENABLED]: (state, { payload: provincePricingEnabled }) => ({
      ...state,
      provincePricingEnabled,
    }),
    [SET_SELECTED_PROVINCE]: (state, { payload: selectedProvince }) => ({
      ...state,
      selectedProvince,
    }),
  },
});

export default configuratorReducer;
